import { useState } from 'react';
import type { Campaign } from '@/mocks/campaigns';

interface PostUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaigns: Campaign[];
}

type UpdateType = 'progress' | 'milestone' | 'news' | 'thanks';

const typeOptions: { key: UpdateType; icon: string; label: string; desc: string; color: string; bg: string }[] = [
  { key: 'milestone', icon: 'ri-trophy-fill',    label: 'إنجاز',  desc: 'وصلت لهدف أو نسبة مميزة', color: 'text-amber-600',   bg: 'bg-amber-50 border-amber-200' },
  { key: 'progress',  icon: 'ri-bar-chart-fill', label: 'تقدم',   desc: 'تحديث عن سير العمل',       color: 'text-sky-600',     bg: 'bg-sky-50 border-sky-200' },
  { key: 'news',      icon: 'ri-megaphone-fill', label: 'خبر',    desc: 'خبر جديد عن الحملة',       color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200' },
  { key: 'thanks',    icon: 'ri-heart-fill',     label: 'شكر',    desc: 'رسالة شكر للداعمين',       color: 'text-rose-500',    bg: 'bg-rose-50 border-rose-200' },
];

const PostUpdateModal = ({ isOpen, onClose, campaigns }: PostUpdateModalProps) => {
  const [step, setStep] = useState<'form' | 'success'>('form');
  const [selectedCampaign, setSelectedCampaign] = useState(campaigns[0]?.id ?? '');
  const [updateType, setUpdateType] = useState<UpdateType>('progress');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [notifyDonors, setNotifyDonors] = useState(true);

  const isValid = selectedCampaign && title.trim().length >= 5 && body.trim().length >= 20;

  const handleSubmit = () => {
    if (!isValid) return;
    setStep('success');
  };

  const handleClose = () => {
    setStep('form');
    setTitle('');
    setBody('');
    setUpdateType('progress');
    setNotifyDonors(true);
    onClose();
  };

  const selectedCampaignData = campaigns.find((c) => c.id === selectedCampaign);
  const activeCfg = typeOptions.find((t) => t.key === updateType)!;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" dir="rtl">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} />

      <div className="relative bg-white rounded-3xl w-full max-w-lg overflow-hidden">

        {/* ── Success State ── */}
        {step === 'success' && (
          <div className="p-8 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-emerald-50 rounded-2xl mx-auto mb-4">
              <i className="ri-check-double-line text-emerald-600 text-3xl"></i>
            </div>
            <h3 className="text-xl font-black text-stone-800 mb-2">تم نشر التحديث!</h3>
            <p className="text-stone-500 text-sm mb-2">
              تم نشر تحديثك على صفحة الحملة بنجاح.
            </p>
            {notifyDonors && (
              <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-6">
                <i className="ri-notification-3-line text-sm"></i>
                تم إرسال إشعار لجميع الداعمين
              </div>
            )}
            <button
              onClick={handleClose}
              className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm cursor-pointer transition-colors whitespace-nowrap"
            >
              رائع، شكراً!
            </button>
          </div>
        )}

        {/* ── Form State ── */}
        {step === 'form' && (
          <>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100">
              <div>
                <h3 className="font-black text-stone-800">نشر تحديث جديد</h3>
                <p className="text-xs text-stone-400 mt-0.5">أخبر داعميك بآخر المستجدات</p>
              </div>
              <button
                onClick={handleClose}
                className="w-8 h-8 flex items-center justify-center rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-500 cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-base"></i>
              </button>
            </div>

            <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">

              {/* Campaign selector */}
              <div>
                <label className="block text-xs font-bold text-stone-600 mb-2">الحملة</label>
                <div className="space-y-2">
                  {campaigns.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => setSelectedCampaign(c.id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all cursor-pointer text-right ${
                        selectedCampaign === c.id
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-stone-100 hover:border-stone-200 bg-white'
                      }`}
                    >
                      <div className="w-10 h-10 rounded-xl overflow-hidden shrink-0">
                        <img src={c.coverImage} alt={c.title} className="w-full h-full object-cover object-top" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-bold truncate ${selectedCampaign === c.id ? 'text-emerald-800' : 'text-stone-700'}`}>
                          {c.title}
                        </p>
                        <p className="text-xs text-stone-400">{c.daysLeft} يوم متبقي · {c.donorsCount.toLocaleString('en-US')} داعم</p>
                      </div>
                      {selectedCampaign === c.id && (
                        <i className="ri-checkbox-circle-fill text-emerald-600 text-lg shrink-0"></i>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Update type */}
              <div>
                <label className="block text-xs font-bold text-stone-600 mb-2">نوع التحديث</label>
                <div className="grid grid-cols-2 gap-2">
                  {typeOptions.map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => setUpdateType(opt.key)}
                      className={`flex items-center gap-2.5 p-3 rounded-xl border-2 transition-all cursor-pointer text-right ${
                        updateType === opt.key ? opt.bg + ' border-current' : 'border-stone-100 bg-white hover:border-stone-200'
                      }`}
                    >
                      <div className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 ${updateType === opt.key ? 'bg-white/60' : 'bg-stone-100'}`}>
                        <i className={`${opt.icon} text-sm ${updateType === opt.key ? opt.color : 'text-stone-400'}`}></i>
                      </div>
                      <div>
                        <p className={`text-xs font-bold ${updateType === opt.key ? opt.color : 'text-stone-700'}`}>{opt.label}</p>
                        <p className="text-xs text-stone-400 leading-tight">{opt.desc}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-xs font-bold text-stone-600 mb-2">
                  عنوان التحديث <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="مثال: وصلنا 80% من الهدف!"
                  maxLength={80}
                  className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm text-stone-800 focus:outline-none focus:border-emerald-400 transition-colors"
                />
                <div className="flex justify-between mt-1">
                  {title.length > 0 && title.length < 5 && (
                    <p className="text-xs text-red-400">5 أحرف على الأقل</p>
                  )}
                  <span className="text-xs text-stone-400 mr-auto">{title.length} / 80</span>
                </div>
              </div>

              {/* Body */}
              <div>
                <label className="block text-xs font-bold text-stone-600 mb-2">
                  تفاصيل التحديث <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="اكتب تفاصيل التحديث هنا... أخبر داعميك بما تحقق وما هي الخطوات القادمة."
                  rows={4}
                  maxLength={500}
                  className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 focus:outline-none focus:border-emerald-400 transition-colors resize-none"
                />
                <div className="flex justify-between mt-1">
                  {body.length > 0 && body.length < 20 && (
                    <p className="text-xs text-red-400">20 حرفاً على الأقل</p>
                  )}
                  <span className="text-xs text-stone-400 mr-auto">{body.length} / 500</span>
                </div>
              </div>

              {/* Preview */}
              {title && body && (
                <div className="bg-stone-50 rounded-2xl overflow-hidden border border-stone-100">
                  <div className="px-3 py-2 bg-stone-100 flex items-center gap-1.5">
                    <i className="ri-eye-line text-stone-400 text-xs"></i>
                    <span className="text-xs text-stone-500 font-medium">معاينة التحديث</span>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full ${activeCfg.bg} ${activeCfg.color}`}>
                        <i className={`${activeCfg.icon} text-xs`}></i>
                        {activeCfg.label}
                      </span>
                      <span className="text-xs text-stone-400">الآن</span>
                    </div>
                    <p className="text-sm font-black text-stone-800 mb-1">{title}</p>
                    <p className="text-xs text-stone-500 leading-relaxed line-clamp-2">{body}</p>
                    {selectedCampaignData && (
                      <div className="flex items-center gap-2 mt-3 pt-2 border-t border-stone-200">
                        <img src={selectedCampaignData.creatorAvatar} alt="" className="w-5 h-5 rounded-full object-cover" />
                        <span className="text-xs text-stone-400">{selectedCampaignData.creatorName}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Notify toggle */}
              <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-2xl border border-emerald-100">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-emerald-100 rounded-xl shrink-0">
                    <i className="ri-notification-3-line text-emerald-600 text-base"></i>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-emerald-800">إشعار الداعمين</p>
                    <p className="text-xs text-emerald-600">
                      إرسال إشعار لـ {selectedCampaignData?.donorsCount.toLocaleString('en-US') ?? 0} داعم
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setNotifyDonors(!notifyDonors)}
                  className={`w-11 h-6 rounded-full transition-colors relative shrink-0 cursor-pointer ${notifyDonors ? 'bg-emerald-500' : 'bg-stone-200'}`}
                >
                  <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${notifyDonors ? 'left-5' : 'left-0.5'}`} />
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-stone-100 flex gap-3">
              <button
                onClick={handleClose}
                className="flex-1 py-3 rounded-xl border border-stone-200 text-stone-600 font-semibold text-sm cursor-pointer hover:bg-stone-50 transition-colors whitespace-nowrap"
              >
                إلغاء
              </button>
              <button
                onClick={handleSubmit}
                disabled={!isValid}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm transition-colors whitespace-nowrap ${
                  isValid
                    ? 'bg-emerald-600 hover:bg-emerald-700 text-white cursor-pointer'
                    : 'bg-stone-100 text-stone-400 cursor-not-allowed'
                }`}
              >
                <i className="ri-send-plane-line"></i>
                نشر التحديث
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PostUpdateModal;
